// @ts-strict

import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@env/environment';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, take, takeUntil, tap } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';
import { IPrivateChatUser, IPrivateChatUsersList, IPrivateMessage } from '../types/private-chat';
import { ChatMessageResponse } from './general-chat/general-chat.service.types';
import { GetUserBasicDataService } from './get-user-basic-data/get-user-basic-data.service';
import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { BaseApiResponse, ISocketPrivateMessage, STATIC_URL } from '..';
import { PrivateChatService } from '@services/private-chat/private-chat.service';
import { isEqual } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class SocketService implements OnDestroy {
  public publicMessage$ = new BehaviorSubject<ChatMessageResponse | null>(null);
  public privateMessage$ = new Subject<IPrivateMessage | null | undefined>();
  public leadNotificationUrl?: string;

  private userID?: number;
  private socketRoom = 'panel';
  private otherNotificationUrl?: string;
  private socket: Socket;
  // private socket: Socket<ServerToClientEvents, ClientToServerEvents> = io(environment.socketioURL, { autoConnect: false });
  private destroy$ = new Subject<void>();
  private connectedToSocket$ = new Subject<boolean>();

  constructor(private localStorageService: LocalStorageService, private userDataService: GetUserBasicDataService, private privateChat: PrivateChatService) {
    this.socket = io(environment.socketioURL, {
      autoConnect: false,
      // withCredentials: true,
      // reconnectionAttempts: 3,
      // reconnection: false,
      reconnectionDelay: 10000,
      transports: ['websocket', 'flashsocket', 'htmlfile', 'xhr-polling', 'jsonp-polling', 'polling', 'webtransport']
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.socket.disconnect();
    this.connectedToSocket$.complete();
  }

  public setupSocketConnection(): void {
    if (environment.socketioURL !== '//node.mylead.biz') {
      this.socket.connect();
      this.socket.on('connect', () => {
        this.connectedToSocket$.next(true);
        this.logUserToSocket();

        // for testing disconnects
        // setTimeout(() => {
        //   this.socket.disconnect();
        // }, 180000);
      });
    }

    // listening to all events
    // this.socket.onAny((event, ...args) => {
    //   console.log(`got ${event}`);
    // });

    this.socket.on('connect_error', err => {
      console.log(err.message); // not authorized
    });

    this.socket.on('disconnect', (reason, details) => {
      console.log(reason, details);
      this.connectedToSocket$.next(false);

      // normally socket will try to reconnect, so don't do this - use if disconnected on purpose (tests etc.)
      // const userData = this.localStorageService.getParsedUserBasicData();
      // if (userData) {
      //   setTimeout(() => this.socket.connect(), 3600);
      // }
    });
  }

  public playLeadSound(): void {
    if (this.leadNotificationUrl) {
      const audio = new Audio(this.leadNotificationUrl);
      audio.play();
    }
  }

  public playNotificationSound(): void {
    if (this.otherNotificationUrl) {
      const audio = new Audio(this.otherNotificationUrl);
      audio.play();
    }
  }

  public on(event: string): Observable<any> {
    return fromEvent(this.socket, event);
  }

  public emit(event: string, ...args: any[]): void {
    this.socket.emit.apply(this.socket, [event, ...args]);
  }

  public sendPublicChatMessage(data: ChatMessageResponse): void {
    this.publicMessage$.next(data);
  }

  public sendPrivateChatMessage(message?: IPrivateMessage): void {
    const userData = this.localStorageService.getParsedUserBasicData();
    if (message) {
      this.emit('chat private', message.target_id, message.user_id, userData?.user?.avatar, message.content, userData?.user?.username, 'normal_group');
    }
    this.privateMessage$.next(message);
  }

  public disconnect(): void {
    this.socket.disconnect();
  }

  private subscribeToNewChatMessage(userId: number) {
    this.on('chat_private_message')
      // .pipe(takeUntil(this.destroy$),takeUntil(this.connectedToSocket$))
      .pipe(takeUntil(this.connectedToSocket$))
      .subscribe((data: ISocketPrivateMessage | null) => {
        if (data) {
          const newMessage: IPrivateMessage = {
            content: data.text,
            created_at: new Date().toISOString(),
            id: data.id,
            shown: null,
            target_id: userId,
            type: data.type,
            user_id: data.user_id,
            socket_message: true
          };

          this.privateMessage$.next(newMessage);
          if (this.privateChat.currentlyChattingWith !== data.username) {
            this.privateChat.unreadMessages$.next((this.privateChat.unreadMessages$.getValue() || 0) + 1);
          }
        }
      });
  }

  private subscribeToPrivateChatList(): void {
    this.privateChat
      .getPrivateChatUsersList()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.privateMessage$
          .pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged((l, r) => isEqual(l, r))
          )
          .subscribe(message => {
            const privateChatUsersList: BaseApiResponse<IPrivateChatUsersList> | undefined = JSON.parse(
              JSON.stringify(this.privateChat.privateChatData$.value)
            );

            if (privateChatUsersList?.data) {
              const user = (privateChatUsersList.data.users || []).find(user => user.user_id === message?.target_id || user.user_id === message?.user_id);
              const support = (privateChatUsersList.data.support || []).find(user => user.user_id === message?.target_id || user.user_id === message?.user_id);

              const updatePrivateChatUsersList = (users: IPrivateChatUser[], message: IPrivateMessage): IPrivateChatUser[] => {
                const { user_id, target_id, content, created_at } = message;

                const updatedUser = users.find(user => user.user_id === message.user_id || user.user_id === message.target_id);

                if (!updatedUser) return users;

                const isFullViewUpdatedUserActive = updatedUser.user_id === this.privateChat.fullviewActiveChatObser.value?.user_id;

                const isConversationPanelUpdatedUserActive = updatedUser.user_id === this.privateChat.currentActiveChat.value?.user_id;

                const newUnreadCount = !isFullViewUpdatedUserActive && !isConversationPanelUpdatedUserActive ? 1 : 0;

                return [
                  {
                    ...updatedUser,
                    content: content || '',
                    created_at: created_at,
                    unread: (updatedUser.unread || 0) + newUnreadCount
                  },
                  ...users.filter(user => user.user_id !== user_id && user.user_id !== target_id)
                ];
              };

              if (user && message) {
                privateChatUsersList.data.users = updatePrivateChatUsersList(privateChatUsersList.data.users ?? [], message);
              }
              if (support && message) {
                privateChatUsersList.data.support = updatePrivateChatUsersList(privateChatUsersList.data.support ?? [], message);
              }
              this.privateChat.privateChatData$.next(privateChatUsersList);
            }
          });
      });
  }

  private logUserToSocket() {
    const userData = this.localStorageService.getParsedUserBasicData();
    if (userData) {
      this.userID = userData.user?.user_id;
      this.leadNotificationUrl = userData.user?.lead_notification_sound;
      this.otherNotificationUrl = STATIC_URL + 'sounds/notifications/base.mp3';
      if (this.userID && this.socketRoom) {
        this.socket.emit('login', this.userID, this.socketRoom);
        this.subscribeToNewChatMessage(this.userID);
        this.subscribeToPrivateChatList();
      }
    } else {
      this.userDataService.shareUserDataSubject
        .pipe(
          filter(result => result !== null),
          take(1)
        )
        .subscribe(res => {
          this.userID = res?.user?.user_id;
          this.leadNotificationUrl = res?.user?.lead_notification_sound;

          if (this.userID && this.socketRoom) {
            this.socket.emit('login', this.userID, this.socketRoom);
            this.subscribeToNewChatMessage(this.userID);
            this.subscribeToPrivateChatList();
          }
        });
    }
  }
}
